import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { formatApiError, money } from "@/lib/api";
import { PageHeader, StatCard, Loading, Empty } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MapPin, Megaphone, FileText, Plus } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const BOARD_TONE = { planned: "bg-slate-100 text-slate-600", installed: "bg-emerald-100 text-emerald-800", removed: "bg-red-100 text-red-800", damaged: "bg-amber-100 text-amber-800" };

export default function Marketing() {
  const qc = useQueryClient();
  const [editBoard, setEditBoard] = useState(null);
  const [pamOpen, setPamOpen] = useState(false);
  const [pam, setPam] = useState({ area: "", quantity: 100, date: "", responsible: "", expense: 0 });

  const { user } = useAuth();
  const { data: boards, isLoading } = useQuery({ queryKey: ["boards"], queryFn: async () => (await api.get("/marketing/boards")).data });
  const { data: pamphlets } = useQuery({ queryKey: ["pamphlets"], queryFn: async () => (await api.get("/marketing/pamphlets")).data });
  const { data: attribution } = useQuery({ queryKey: ["attribution"], queryFn: async () => (await api.get("/marketing/attribution")).data, enabled: ["admin", "marketing"].includes(user?.role) });
  const { data: expenses } = useQuery({ queryKey: ["mexpenses"], queryFn: async () => (await api.get("/marketing/expenses")).data, enabled: user?.role === "admin" });

  const saveBoard = useMutation({
    mutationFn: async () => (await api.put(`/marketing/boards/${editBoard.code}`, { location: editBoard.location, landmark: editBoard.landmark, status: editBoard.status, installation_date: editBoard.installation_date, permission_note: editBoard.permission_note, cost: Number(editBoard.cost || 0), photo_url: editBoard.photo_url || "" })).data,
    onSuccess: () => { toast.success("Board updated"); setEditBoard(null); qc.invalidateQueries(); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const addPam = useMutation({
    mutationFn: async () => (await api.post("/marketing/pamphlets", { ...pam, quantity: Number(pam.quantity), expense: Number(pam.expense) })).data,
    onSuccess: () => { toast.success("Pamphlet log added"); setPamOpen(false); qc.invalidateQueries(); },
  });

  return (
    <div>
      <PageHeader title="Marketing" subtitle="Offline attribution — flex boards & pamphlets. Attribution uses first source; each admission counted once." testid="marketing-header" />
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        <StatCard label="Flex boards spend" value={money(expenses?.flex_total || 0)} icon={Megaphone} tone="blue" testid="flex-spend" />
        <StatCard label="Pamphlet spend" value={money(expenses?.pamphlet_total || 0)} icon={FileText} tone="orange" testid="pamphlet-spend" />
        <StatCard label="Total marketing spend" value={money(expenses?.total || 0)} icon={Megaphone} tone="slate" testid="total-spend" />
      </div>

      <Tabs defaultValue="boards">
        <TabsList>
          <TabsTrigger value="boards" data-testid="tab-boards">Flex Boards (B001–B100)</TabsTrigger>
          <TabsTrigger value="pamphlets" data-testid="tab-pamphlets">Pamphlets</TabsTrigger>
          <TabsTrigger value="attribution" data-testid="tab-attribution">Attribution</TabsTrigger>
        </TabsList>

        <TabsContent value="boards" className="mt-4">
          {isLoading ? <Loading /> : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
              {boards.map((b) => (
                <button key={b.code} onClick={() => setEditBoard({ ...b, cost: (b.cost_paise || 0) / 100 })} className="p-3 rounded-lg border bg-white dark:bg-slate-800 text-left hover:shadow-md transition-shadow" data-testid={`board-${b.code}`}>
                  <div className="flex items-center justify-between"><span className="font-mono font-bold text-sm">{b.code}</span><span className={`text-[9px] px-1.5 py-0.5 rounded-full ${BOARD_TONE[b.status]}`}>{b.status}</span></div>
                  <div className="text-[11px] text-slate-500 mt-1 truncate flex items-center gap-1"><MapPin className="w-3 h-3" />{b.location || "location unknown"}</div>
                </button>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="pamphlets" className="mt-4">
          <Button className="mb-3 bg-brand-blue hover:bg-blue-700 gap-2" onClick={() => setPamOpen(true)} data-testid="add-pamphlet-btn"><Plus className="w-4 h-4" /> Log distribution</Button>
          {!pamphlets?.length ? <Empty title="No pamphlet logs" /> : (
            <div className="rounded-xl border bg-white dark:bg-slate-800 overflow-x-auto">
              <table className="w-full text-sm"><thead className="bg-slate-50 dark:bg-slate-900 text-xs uppercase text-slate-500"><tr><th className="text-left px-4 py-3">Area</th><th className="text-left px-4 py-3">Qty</th><th className="text-left px-4 py-3">Date</th><th className="text-left px-4 py-3">By</th><th className="text-left px-4 py-3">Expense</th></tr></thead>
                <tbody>{pamphlets.map((p) => <tr key={p.id} className="border-t"><td className="px-4 py-3">{p.area}</td><td className="px-4 py-3">{p.quantity}</td><td className="px-4 py-3">{p.date}</td><td className="px-4 py-3">{p.responsible}</td><td className="px-4 py-3">{money((p.expense_paise || 0) / 100)}</td></tr>)}</tbody>
              </table>
            </div>
          )}
        </TabsContent>

        <TabsContent value="attribution" className="mt-4">
          <div className="rounded-xl border bg-white dark:bg-slate-800 overflow-x-auto">
            <table className="w-full text-sm"><thead className="bg-slate-50 dark:bg-slate-900 text-xs uppercase text-slate-500"><tr><th className="text-left px-4 py-3">Source</th><th className="text-left px-4 py-3">Enquiries</th><th className="text-left px-4 py-3">Visits</th><th className="text-left px-4 py-3">Paid admissions</th></tr></thead>
              <tbody>{(attribution?.rows || []).filter((r) => r.enquiries > 0).map((r) => <tr key={r.source} className="border-t"><td className="px-4 py-3 capitalize font-medium">{r.source}</td><td className="px-4 py-3">{r.enquiries}</td><td className="px-4 py-3">{r.visits}</td><td className="px-4 py-3 font-semibold text-emerald-600">{r.paid_admissions}</td></tr>)}</tbody>
            </table>
          </div>
          <p className="text-xs text-slate-400 mt-2">{attribution?.note}</p>
        </TabsContent>
      </Tabs>

      {editBoard && (
        <Dialog open onOpenChange={() => setEditBoard(null)}>
          <DialogContent>
            <DialogHeader><DialogTitle>Board {editBoard.code} • {editBoard.size}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Location</Label><Input value={editBoard.location} onChange={(e) => setEditBoard((b) => ({ ...b, location: e.target.value }))} data-testid="board-location" /></div>
              <div><Label>Landmark</Label><Input value={editBoard.landmark} onChange={(e) => setEditBoard((b) => ({ ...b, landmark: e.target.value }))} data-testid="board-landmark" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Status</Label>
                  <Select value={editBoard.status} onValueChange={(v) => setEditBoard((b) => ({ ...b, status: v }))}>
                    <SelectTrigger data-testid="board-status"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="planned">Planned</SelectItem><SelectItem value="installed">Installed</SelectItem><SelectItem value="damaged">Damaged</SelectItem><SelectItem value="removed">Removed</SelectItem></SelectContent>
                  </Select>
                </div>
                <div><Label>Cost ₹</Label><Input type="number" value={editBoard.cost} onChange={(e) => setEditBoard((b) => ({ ...b, cost: e.target.value }))} data-testid="board-cost" /></div>
              </div>
              <div><Label>Installation date</Label><Input type="date" value={editBoard.installation_date} onChange={(e) => setEditBoard((b) => ({ ...b, installation_date: e.target.value }))} data-testid="board-install-date" /></div>
              <div><Label>Permission / contact note</Label><Input value={editBoard.permission_note} onChange={(e) => setEditBoard((b) => ({ ...b, permission_note: e.target.value }))} data-testid="board-permission" /></div>
            </div>
            <DialogFooter><Button className="bg-brand-blue hover:bg-blue-700" onClick={() => saveBoard.mutate()} data-testid="board-save">Save</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {pamOpen && (
        <Dialog open onOpenChange={setPamOpen}>
          <DialogContent>
            <DialogHeader><DialogTitle>Log pamphlet distribution</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Area *</Label><Input value={pam.area} onChange={(e) => setPam((p) => ({ ...p, area: e.target.value }))} data-testid="pam-area" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Quantity</Label><Input type="number" value={pam.quantity} onChange={(e) => setPam((p) => ({ ...p, quantity: e.target.value }))} data-testid="pam-qty" /></div>
                <div><Label>Date</Label><Input type="date" value={pam.date} onChange={(e) => setPam((p) => ({ ...p, date: e.target.value }))} data-testid="pam-date" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Responsible</Label><Input value={pam.responsible} onChange={(e) => setPam((p) => ({ ...p, responsible: e.target.value }))} data-testid="pam-resp" /></div>
                <div><Label>Expense ₹</Label><Input type="number" value={pam.expense} onChange={(e) => setPam((p) => ({ ...p, expense: e.target.value }))} data-testid="pam-expense" /></div>
              </div>
            </div>
            <DialogFooter><Button className="bg-brand-blue hover:bg-blue-700" disabled={!pam.area} onClick={() => addPam.mutate()} data-testid="pam-save">Add</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
